import { ExcelSheetIcon } from '@assets';
import {
  Box,
  Stack,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@atgfr8/atg-ui-tailwind';
import {
  ContentCopyIcon,
  DeleteIcon,
  DownloadIcon,
  PlusCircleOutlineIcon,
} from '@atgfr8/icons';
import { equipmentsArray } from '@common/commonLists';
import {
  AutoComplete,
  BasicModal,
  BasicTable,
  Button,
  SelectInput,
  ToastType,
} from '@components';
import { AtgLoader } from '@components/AtgLoader';
import CommonFileUpload from '@components/FileUpload/CommonFileUpload';
import { customerUrl } from '@constants/urls';
import { useParams } from '@solidjs/router';
import {
  customerDetailsStore,
  getCustomerData,
  Quote,
  setCustomerDetailsStore,
  uploadQuoteFile,
} from '@store/customers/customerDetails';
import { closeModal, openModal } from '@store/modals';
import { prospectDetailsStore, setProspectDetailsStore } from '@store/prospect';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { handleToast, isFullAdmin } from '@utils/utils';
import { COUNTRY_CODES } from '@constants/countryCodes';
import { orderBy } from 'lodash';
import { DateTime } from 'luxon';
import { Component, createSignal, Index, Show } from 'solid-js';

const columns = [
  {
    key: '',
  },
  {
    key: 'origin',
    name: 'Origin',
  },
  {
    key: 'destination',
    name: 'Destination',
  },
  {
    key: 'price',
    name: 'Rate',
  },
  {
    key: 'equipmentId',
    name: 'Equipment',
  },
  {
    key: 'notes',
    name: 'Notes',
  },
  {
    key: 'createdDate',
    name: 'Created Date',
    minWidth: '120px',
  },
];

type Location = {
  display: string;
  city: string;
  country: string;
  state: string;
  zipcode: string;
};

type QuotesProps = {
  type: 'Customer' | 'Prospect';
};

export const Quotes: Component<QuotesProps> = (props) => {
  const [file, setFile] = createSignal<
    {
      name: string;
      size: number;
    }[]
  >([]);
  const [sortConfig, setSortConfig] = createSignal<{
    key: string;
    order: 'asc' | 'desc';
  }>({ key: '', order: 'desc' });

  const { id } = useParams();

  const footerRow = [
    {
      renderCell: () => (
        <span>
          <PlusCircleOutlineIcon
            color="#0070A2"
            style={{ cursor: 'pointer' }}
            onClick={handleAddQuote}
          />
        </span>
      ),
      key: 'ideaTitle',
    },

    {
      key: '#',
    },
    {
      key: '#',
    },
    {
      key: '#',
    },
    {
      key: '#',
    },
    {
      key: '#',
    },
    {
      key: '#',
    },
  ];

  const getNewSmallestId = () => {
    let smallestId = 0;
    (props.type === 'Customer'
      ? customerDetailsStore.customer.quotes
      : prospectDetailsStore.prospectDetails.quotes
    ).forEach((quote) => {
      if (quote.id < smallestId) {
        smallestId = quote.id;
      }
    });
    return smallestId - 1;
  };

  const handleAddQuote = () => {
    const defaultQuote: Quote = {
      operationType: 'Insert',
      id: getNewSmallestId(),
      createdDate: DateTime.now().toFormat('M/d/yyyy h:mm:ss'),
      customerId:
        props.type === 'Customer' ? customerDetailsStore.customer.id : null,
      destination: null,
      destinationCity: null,
      destinationState: null,
      destinationZip: null,
      equipmentId: null,
      notes: null,
      origin: null,
      originCity: null,
      originState: null,
      originZip: null,
      price: 0,
      prospectId: null,
      originCountryCode: 'US',
      destinationCountryCode: 'US',
    };
    if (props.type === 'Customer') {
      setCustomerDetailsStore('customer', 'quotes', [
        ...customerDetailsStore.customer.quotes,
        defaultQuote,
      ]);
    } else {
      setProspectDetailsStore('prospectDetails', 'quotes', [
        ...prospectDetailsStore.prospectDetails.quotes,
        defaultQuote,
      ]);
    }
  };

  const handleCopyQuote = (index: number) => {
    const quotes = [
      ...customerDetailsStore.customer.quotes,
      {
        ...customerDetailsStore.customer.quotes[index],
        id: getNewSmallestId(),
        operationType: 'Insert',
      },
    ];
    setCustomerDetailsStore('customer', 'quotes', quotes);
  };

  const handleDeleteQuote = (quoteId: number) => {
    const quotes =
      props.type === 'Prospect'
        ? [...prospectDetailsStore.prospectDetails.quotes]
        : [...customerDetailsStore.customer.quotes];
    const findIndex = quotes.findIndex((quote) => quote.id === quoteId);
    if (findIndex !== -1) {
      if (quotes[findIndex].operationType === 'Insert') {
        quotes.splice(findIndex, 1);
      } else {
        quotes[findIndex] = {
          ...quotes[findIndex],
          operationType: 'Delete',
        };
      }
    }
    props.type === 'Prospect'
      ? setProspectDetailsStore('prospectDetails', 'quotes', quotes)
      : setCustomerDetailsStore('customer', 'quotes', quotes);
  };

  const handleChange = (
    id: number,
    key: string,
    value: string | number | Location,
  ) => {
    const quotes = (
      props.type === 'Customer'
        ? customerDetailsStore.customer.quotes
        : prospectDetailsStore.prospectDetails.quotes
    ).map((quote) => {
      if (quote.id === id) {
        if (key === 'origin' || key === 'destination') {
          if (value === '') {
            return {
              ...quote,
              [key]: '',
              [key + 'City']: '',
              [key + 'State']: '',
              [key + 'Zip']: '',
            };
          }
          return {
            ...quote,
            [key]: (value as Location).display,
            [key + 'City']: (value as Location).city,
            [key + 'State']: (value as Location).state,
            [key + 'Zip']: (value as Location).zipcode,
          };
        }
        return { ...quote, [key]: value };
      }
      return quote;
    });

    if (props.type === 'Customer') {
      setCustomerDetailsStore('customer', 'quotes', quotes);
    } else {
      setProspectDetailsStore('prospectDetails', 'quotes', quotes);
    }
  };

  const handleFileChange = async (file: File) => {
    try {
      setFile((prev) => {
        return [
          ...prev,
          {
            name: file.name,
            size: file.size,
          },
        ];
      });
      setCustomerDetailsStore('isLoading', true);
      const res = await uploadQuoteFile(id as string, file);

      if (Boolean(res)) {
        await getCustomerData(id as string);
      }
    } catch (error) {
      handleToast(ToastType.Error, 'Failed to upload quote file');
      setFile((prev) => {
        const files = [...prev];
        const fileIndex = files.findIndex(
          (f) => f.name === file.name && f.size === file.size,
        );
        if (fileIndex !== -1) {
          files.splice(fileIndex, 1);
        }
        return files;
      });
    } finally {
      setCustomerDetailsStore('isLoading', false);
      setFile([]);
      closeModal('importQuoteModal');
    }
  };

  const allIdsGreaterThanZero = () =>
    prospectDetailsStore.prospectDetails.quotes.every((quote) => quote.id > 0);

  const dataSource = () => {
    if (props.type === 'Customer') {
      return customerDetailsStore.customer.quotes;
    }

    if (allIdsGreaterThanZero()) {
      if (sortConfig().key === 'equipmentId') {
        const sortedQuotes = prospectDetailsStore.prospectDetails.quotes.map(
          (quote) => {
            const equipment = equipmentsArray.find(
              (e) => e.value === String(quote.equipmentId),
            );
            return {
              ...quote,
              equipmentLabel: equipment ? equipment.label : '',
            };
          },
        );
        return orderBy(sortedQuotes, 'equipmentLabel', sortConfig().order);
      }
      return orderBy(
        prospectDetailsStore.prospectDetails.quotes,
        sortConfig().key,
        sortConfig().order,
      );
    }
    return prospectDetailsStore.prospectDetails.quotes;
  };

  const handleSortBy = (key: string) => {
    setSortConfig((prev) => ({
      key,
      order: prev.key === key && prev.order === 'asc' ? 'desc' : 'asc',
    }));
  };

  return (
    <Stack class="h-[calc(100vh-210px)]">
      <Show
        when={customerDetailsStore.isLoading || prospectDetailsStore.loading}
      >
        <AtgLoader />
      </Show>
      <Show when={props.type === 'Customer'}>
        <Box
          backgroundColor="#F8F8F8"
          style={{
            border: '1px solid #0000001F',
            'border-bottom': '0',
            'border-color': '#0000001F',
          }}
          p={3}
          display="flex"
          justifyContent="end"
        >
          <Button
            startIcon={
              <img src={ExcelSheetIcon} height="18px" alt="Excel Sheet" />
            }
            variant="contained"
            label="Import"
            type="button"
            onClick={() => {
              openModal('importQuoteModal');
            }}
            dataTestId="button-import-quote-model"
          />
        </Box>
      </Show>
      <BasicTable<Quote>
        footerRow={footerRow}
        columnNoWrap
        tableContainerClasses="overflow-auto w-full! h-full border text-black! [&_td]:!border-[#e0e0e0] [&_th]:!border-[#e0e0e0]"
      >
        <TableRow class="sticky! top-0 bg-white shadow-md! z-10  w-full">
          {columns.map((column) => {
            const isSortableHeader =
              props.type === 'Prospect' &&
              allIdsGreaterThanZero() &&
              Boolean(column.key);
            const isSortedHeader =
              isSortableHeader && sortConfig().key === column.key;

            return (
              <TableCell
                onClick={() => handleSortBy(column.key)}
                class={`px-1! py-2! h-[40.5px]! ${
                  isSortableHeader ? 'cursor-pointer' : ''
                }`}
                style={{ 'min-width': column.minWidth }}
              >
                <Typography variant="body1" class="font-bold! text-black!">
                  {column.name}
                  {isSortedHeader && (
                    <span class="ml-1">
                      {sortConfig().order === 'asc' ? '▲' : '▼'}
                    </span>
                  )}
                </Typography>
              </TableCell>
            );
          })}
        </TableRow>
        <Index each={dataSource()}>
          {(rowData, index) => (
            <Show when={rowData().operationType !== 'Delete'}>
              <TableRow>
                <TableCell
                  class={`py-3! px-1! align-middle! ${props.type === 'Customer' ? 'w-[8%]' : 'w-[3%]'}`}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gap={1}
                  >
                    <DeleteIcon
                      class="cursor-pointer"
                      onClick={() => {
                        handleDeleteQuote(rowData().id);
                      }}
                      color="#b00020"
                    />
                    <Show when={props.type === 'Customer'}>
                      <Button
                        onClick={() => handleCopyQuote(index)}
                        label="Copy"
                        startIcon={<ContentCopyIcon size={20} />}
                        size="sm"
                        dataTestId={formatDataTestId('Copy', 'button')}
                      />
                    </Show>
                  </Box>
                </TableCell>
                <TableCell class="py-3! px-1! w-[18%]! align-top! min-w-[310px]!">
                  <Box class="flex gap-2">
                    <AutoComplete
                      innerRefClass="w-full"
                      id={`autocomplete-customer-details-quote-origin-${
                        rowData().id
                      }`}
                      endpoint={`capacity/autocomplete?${
                        rowData().originCountryCode === 'other'
                          ? ''
                          : `country=${rowData().originCountryCode}&`
                      }term`}
                      onItemSelect={(item) => {
                        const item1 = item as Location;
                        handleChange(rowData().id, 'origin', item1);
                      }}
                      value={rowData().origin ?? ''}
                      label="Origin"
                      version="v4"
                      onClearValue={() =>
                        handleChange(rowData().id, 'origin', '')
                      }
                      responseFieldName="display"
                      disabled={rowData().id > 0}
                      placeholder="Add Origin"
                    />
                    <Show when={rowData().id < 0 && props.type === 'Prospect'}>
                      <Box class="w-[110px]">
                        <SelectInput
                          menuItems={[
                            ...COUNTRY_CODES.map((c) => ({
                              label: c.label,
                              value: String(c.value),
                            })),
                            {
                              value: 'other',
                              label: 'Other',
                            },
                          ]}
                          label="Country"
                          value={rowData().originCountryCode!}
                          onChange={(val) => {
                            handleChange(rowData().id, 'origin', '');
                            handleChange(
                              rowData().id,
                              'originCountryCode',
                              val,
                            );
                          }}
                          style={{ height: '40px' }}
                          dataTestId={formatDataTestId(
                            'Quote origin country',
                            'selectInput',
                          )}
                        />
                      </Box>
                    </Show>
                  </Box>
                </TableCell>
                <TableCell class="py-3! px-1! w-[18%]! align-top! min-w-[310px]!">
                  <Box class="flex gap-2">
                    <AutoComplete
                      innerRefClass="w-full"
                      id={`autocomplete-customer-details-quote-destination-${
                        rowData().id
                      }`}
                      endpoint={`capacity/autocomplete?${
                        rowData().destinationCountryCode === 'other'
                          ? ''
                          : `country=${rowData().destinationCountryCode}&`
                      }term`}
                      onItemSelect={(item) => {
                        const item1 = item as Location;
                        handleChange(rowData().id, 'destination', item1);
                      }}
                      value={rowData().destination ?? ''}
                      label="Destination"
                      version="v4"
                      responseFieldName="display"
                      onClearValue={() => {
                        handleChange(rowData().id, 'destination', '');
                      }}
                      disabled={rowData().id > 0}
                      placeholder="Add Destination"
                    />
                    <Show when={rowData().id < 0 && props.type === 'Prospect'}>
                      <Box class="w-[110px]">
                        <SelectInput
                          menuItems={[
                            ...COUNTRY_CODES.map((c) => ({
                              label: c.label,
                              value: String(c.value),
                            })),
                            {
                              value: 'other',
                              label: 'Other',
                            },
                          ]}
                          label="Country"
                          value={rowData().destinationCountryCode!}
                          onChange={(val) => {
                            handleChange(rowData().id, 'destination', '');
                            handleChange(
                              rowData().id,
                              'destinationCountryCode',
                              val,
                            );
                          }}
                          style={{ height: '40px' }}
                          dataTestId={formatDataTestId(
                            'Quote destination country',
                            'selectInput',
                          )}
                        />
                      </Box>
                    </Show>
                  </Box>
                </TableCell>
                <TableCell class="py-3! px-1! w-[14%]! align-top! min-w-[140px]!">
                  <TextField
                    showInCurrencyFormat
                    type="number"
                    value={rowData().price as number}
                    onChange={(val: unknown) =>
                      handleChange(rowData().id, 'price', val as number)
                    }
                    class="h-[40px]!"
                    label="Rate"
                    size="sm"
                    disabled={rowData().id > 0}
                    placeholder="Add Rate"
                    maxLength={17}
                    dataTestId={`customer-details-quotes-rate-textInput-${rowData().id}`}
                  />
                </TableCell>
                <TableCell class="py-3! px-1! w-[18%]! align-top! min-w-[200px]!">
                  <SelectInput
                    style={{
                      height: '40px',
                    }}
                    value={
                      equipmentsArray.find(
                        (e) => e.value === String(rowData().equipmentId),
                      )?.label ?? ''
                    }
                    menuItems={equipmentsArray.map((e) => ({
                      label: e.label,
                      value: e.label,
                    }))}
                    onChange={(val) => {
                      const equipment = equipmentsArray.find(
                        (e) => e.label === val,
                      );
                      handleChange(
                        rowData().id,
                        'equipmentId',
                        equipment?.value ?? '',
                      );
                    }}
                    disabled={rowData().id > 0}
                    placeholder="Select Equipment"
                    dataTestId={formatDataTestId(
                      'Quote equipment',
                      'selectInput',
                    )}
                  />
                </TableCell>
                <TableCell class="py-3! px-1! w-[14%]! align-top! min-w-[200px]!">
                  <TextField
                    class="h-[40px]!"
                    value={rowData().notes ?? ''}
                    onChange={(val: string) =>
                      handleChange(rowData().id, 'notes', val)
                    }
                    label="Notes"
                    size="sm"
                    disabled={rowData().id > 0}
                    placeholder="Add Notes"
                    maxLength={1000}
                    dataTestId={`customer-details-quotes-notes-textInput-${rowData().id}`}
                  />
                </TableCell>
                <TableCell class="py-3! px-1! w-[10%]!">
                  {DateTime.fromFormat(
                    rowData().createdDate!,
                    'M/d/yyyy h:mm:ss',
                  ).toFormat('M/d/yyyy')}
                </TableCell>
              </TableRow>
            </Show>
          )}
        </Index>
      </BasicTable>
      <BasicModal
        id="importQuoteModal"
        title="Quote Import"
        width="600px"
        footer={false}
      >
        <CommonFileUpload
          height={5}
          uploadedFiles={file()}
          acceptedFileTypes={['csv']}
          shouldUploadOnSelect
          apiCallBackFn={handleFileChange}
          removeFile={(index) => {
            setFile((prev) => {
              const files = [...prev];
              files.splice(index, 1);
              return files;
            });
          }}
          maxFileSize={5242880}
          fileRestrictionMessage="From here you can bulk import your quotes. The file must be a .csv and 5MB or less."
          isAdmin={isFullAdmin()}
        />
        <Box display="flex" justifyContent="end" mt={2}>
          <Button
            startIcon={<DownloadIcon size={20} />}
            label="Download Sample File"
            variant="contained"
            type="button"
            onClick={() => {
              window.open(customerUrl.customerQuoteCsvDownloadLink(), '_blank');
            }}
            dataTestId="sample-file-download-btn-customer"
          />
        </Box>
      </BasicModal>
    </Stack>
  );
};
